import {Component, OnInit} from '@angular/core';
import {HttpClient} from '@angular/common/http';

export interface NumeroMiaGestor {
  id: number;
  nome: string;
  telefone: string;
  whatsappId: string;
  status: string;
  empresa: {id: number; nome: string} | null;
  miaGestor: boolean;
  sdrContatos: string;
  /** número de exemplo da Mia: contatos dele testam outra empresa (demonstração por contato) */
  exemploMia: boolean;
}

export interface NumeroV4MiaGestor {
  id: number;
  telefone: string;
  empresa: {id: number; nome: string} | null;
  miaGestor: boolean;
  sdrContatos: string;
  ativadoEm: string | null;
  /** follow-up automático (D1, D3, D7) para leads que param de responder */
  followup: boolean;
}

/**
 * Qual número Cloud é a Mia Gestor e quem pode conversar com ela.
 * Só o superadmin salva; o servidor garante um único número com o papel.
 */
@Component({
  selector: 'app-numeros-mia-gestor',
  templateUrl: './numeros-mia-gestor.component.html',
  styleUrls: ['./numeros-mia-gestor.component.scss'],
})
export class NumerosMiaGestorComponent implements OnInit {
  numeros: NumeroMiaGestor[] = [];
  fallbackAmbiente: string | null = null;
  escolhidoId: number | null = null;
  /** 'todos' | 'lista' */
  quemConversa = 'todos';
  lista: string[] = [];
  novoTelefone = '';
  carregando = false;
  salvando = false;
  erro = '';
  aviso = '';
  filtro = '';
  salvandoExemplo = false;

  // ===== WhatsApp pessoal pela extensão V4 (leads do site) =====
  numerosV4: NumeroV4MiaGestor[] = [];
  escolhidoV4: NumeroV4MiaGestor | null = null;
  /** 'todos' | 'lista' */
  quemConversaV4 = 'lista';
  listaV4: string[] = [];
  novoTelefoneV4 = '';
  followupV4 = false;
  salvandoV4 = false;
  erroV4 = '';
  avisoV4 = '';

  constructor(private http: HttpClient) {}

  ngOnInit() { this.carregue(); this.carregueV4(); }

  carregueV4() {
    this.http.get<{numeros: NumeroV4MiaGestor[]}>('/gestor-ia/numeros-v4').subscribe({
      next: r => {
        this.numerosV4 = r.numeros || [];
        const atual = this.numerosV4.find(n => n.miaGestor) || (this.numerosV4.length === 1 ? this.numerosV4[0] : null);
        if (atual) this.escolhaV4(atual);
      },
      error: () => { this.erroV4 = 'Não foi possível carregar os números da extensão V4.'; },
    });
  }

  escolhaV4(n: NumeroV4MiaGestor) {
    this.escolhidoV4 = n;
    this.avisoV4 = '';
    this.quemConversaV4 = n.sdrContatos === '*' ? 'todos' : 'lista';
    this.listaV4 = this.telefonesDe(n.sdrContatos);
    this.novoTelefoneV4 = '';
    this.followupV4 = !!n.followup;
  }

  get atualV4(): NumeroV4MiaGestor | null {
    return this.numerosV4.find(n => n.miaGestor) || null;
  }

  /** número ativo com a lista mexida (número inativo não tem o que descartar: o botão é ativar) */
  get mudouV4(): boolean {
    const n = this.escolhidoV4;
    if (!n?.miaGestor) return false;
    if (this.followupV4 !== !!n.followup) return true;
    return this.contatosDe(this.quemConversaV4, this.listaV4, this.novoTelefoneV4) !== (n.sdrContatos || '');
  }

  descarteV4() { if (this.escolhidoV4) this.escolhaV4(this.escolhidoV4); }

  salveV4(miaGestor: boolean) {
    if (!this.escolhidoV4 || this.salvandoV4) return;
    this.salvandoV4 = true; this.erroV4 = ''; this.avisoV4 = '';
    const sdrContatos = this.contatosDe(this.quemConversaV4, this.listaV4, this.novoTelefoneV4);
    this.http.put<{numero: NumeroV4MiaGestor}>(`/gestor-ia/numeros-v4/${this.escolhidoV4.id}`, {miaGestor, sdrContatos, followup: this.followupV4}).subscribe({
      next: r => {
        this.salvandoV4 = false;
        this.numerosV4 = this.numerosV4.map(n => n.id === r.numero.id ? r.numero : n);
        this.escolhaV4(r.numero);
        this.avisoV4 = miaGestor
          ? 'Número pessoal salvo. A Mia Gestor atende os leads autorizados a partir das próximas mensagens.'
          : 'A Mia Gestor deixou de atender por este número.';
      },
      error: e => { this.salvandoV4 = false; this.erroV4 = e?.error?.erro || 'Não foi possível salvar.'; },
    });
  }

  get filtrados() {
    const termo = this.filtro.trim().toLocaleLowerCase();
    if (!termo) return this.numeros;
    return this.numeros.filter(n =>
      `${n.nome} ${n.telefone} ${n.whatsappId} ${n.empresa?.nome || ''}`.toLocaleLowerCase().includes(termo));
  }

  get atual(): NumeroMiaGestor | null {
    return this.numeros.find(n => n.miaGestor) || null;
  }

  get escolhido(): NumeroMiaGestor | null {
    return this.numeros.find(n => n.id === this.escolhidoId) || null;
  }

  get mudou() {
    const atual = this.atual;
    if ((atual?.id || null) !== this.escolhidoId) return true;
    if (!atual) return false;
    return this.contatosDe(this.quemConversa, this.lista, this.novoTelefone) !== (atual.sdrContatos || '');
  }

  async carregue() {
    this.carregando = true; this.erro = '';
    try {
      const r: any = await this.http.get('/gestor-ia/numeros').toPromise();
      this.numeros = r.numeros || [];
      this.fallbackAmbiente = r.fallbackAmbiente || null;
      this.descarte();
    } catch (e: any) {
      this.erro = e?.error?.erro || 'Não foi possível carregar os números.';
    } finally { this.carregando = false; }
  }

  escolha(n: NumeroMiaGestor) {
    if (this.salvando) return;
    this.escolhidoId = n.id;
    this.aviso = '';
    this.rolePara('canal-cloud');
  }

  /** volta o formulário do Cloud para o que está salvo */
  descarte() {
    const atual = this.atual;
    this.escolhidoId = atual ? atual.id : null;
    this.quemConversa = atual && atual.sdrContatos !== '*' && atual.sdrContatos ? 'lista' : 'todos';
    this.lista = this.telefonesDe(atual?.sdrContatos);
    this.novoTelefone = '';
  }

  // ===== lista de telefones em chips (os dois canais) =====

  private telefonesDe(sdrContatos: string | null | undefined): string[] {
    return sdrContatos && sdrContatos !== '*' ? sdrContatos.split(',').filter(Boolean) : [];
  }

  /** "(62) 99999-9999, 5548…" -> dígitos com DDI; sem DDI assume Brasil */
  private normalize(texto: string): string[] {
    return String(texto || '').split(/[,;\n]/)
      .map(v => v.replace(/\D/g, ''))
      .filter(d => d.length >= 10)
      .map(d => d.length <= 11 ? `55${d}` : d);
  }

  /** o que ficou digitado e não foi adicionado também conta ao salvar */
  private contatosDe(quem: string, lista: string[], digitado: string): string {
    if (quem === 'todos') return '*';
    return Array.from(new Set([...lista, ...this.normalize(digitado)])).join(',');
  }

  adicione(canal: 'cloud' | 'v4') {
    if (canal === 'v4') {
      this.listaV4 = Array.from(new Set([...this.listaV4, ...this.normalize(this.novoTelefoneV4)]));
      this.novoTelefoneV4 = '';
    } else {
      this.lista = Array.from(new Set([...this.lista, ...this.normalize(this.novoTelefone)]));
      this.novoTelefone = '';
    }
  }

  remova(canal: 'cloud' | 'v4', telefone: string) {
    if (canal === 'v4') this.listaV4 = this.listaV4.filter(t => t !== telefone);
    else this.lista = this.lista.filter(t => t !== telefone);
  }

  /** 5562982301144 -> +55 62 98230-1144; o que já vem formatado passa direto */
  formateTelefone(telefone: string | null | undefined): string {
    const t = String(telefone || '');
    if (/[^\d+]/.test(t)) return t;
    const digitos = t.replace(/\D/g, '');
    // o número pareado na V4 pode vir sem DDI (62982301144): 10–11 dígitos é Brasil
    const d = digitos.length === 10 || digitos.length === 11 ? `55${digitos}` : digitos;
    const m = d.match(/^55(\d{2})(\d{4,5})(\d{4})$/);
    return m ? `+55 ${m[1]} ${m[2]}-${m[3]}` : (d ? `+${d}` : '');
  }

  quantosTelefones(sdrContatos: string): number { return this.telefonesDe(sdrContatos).length; }

  rolePara(id: string) { document.getElementById(id)?.scrollIntoView({behavior: 'smooth', block: 'start'}); }

  async salve() {
    if (!this.escolhidoId || this.salvando) return;
    this.salvando = true; this.erro = ''; this.aviso = '';
    try {
      await this.http.put(`/gestor-ia/numeros/${this.escolhidoId}`,
        {miaGestor: true, sdrContatos: this.contatosDe(this.quemConversa, this.lista, this.novoTelefone)}).toPromise();
      await this.carregue();
      this.aviso = 'Número da Mia Gestor salvo. Vale para as próximas mensagens, sem reiniciar nada.';
    } catch (e: any) {
      this.erro = e?.error?.erro || 'Não foi possível salvar.';
    } finally { this.salvando = false; }
  }

  /** liga/desliga o número de exemplo da Mia do número escolhido; independe do papel Mia Gestor */
  async alterneExemploMia() {
    const escolhido = this.escolhido;
    if (!escolhido || this.salvando || this.salvandoExemplo) return;
    const ligar = !escolhido.exemploMia;
    this.salvandoExemplo = true; this.erro = ''; this.aviso = '';
    try {
      const r: any = await this.http.put(`/gestor-ia/numeros/${escolhido.id}/exemplo-mia`, {exemploMia: ligar}).toPromise();
      escolhido.exemploMia = !!r?.numero?.exemploMia;
      this.aviso = ligar
        ? 'Número de exemplo da Mia ligado. A seção "Demonstração da MIA" já aparece nos contatos desta empresa, sem reiniciar nada.'
        : 'Número de exemplo da Mia desligado. Os contatos associados voltam ao atendimento normal na próxima mensagem.';
    } catch (e: any) {
      this.erro = e?.error?.erro || 'Não foi possível alterar o número de exemplo.';
    } finally { this.salvandoExemplo = false; }
  }

  async desligue() {
    const atual = this.atual;
    if (!atual || this.salvando) return;
    this.salvando = true; this.erro = ''; this.aviso = '';
    try {
      await this.http.put(`/gestor-ia/numeros/${atual.id}`, {miaGestor: false}).toPromise();
      await this.carregue();
      this.aviso = 'Nenhum número é a Mia Gestor agora. Ele volta a atender como número de loja.';
    } catch (e: any) {
      this.erro = e?.error?.erro || 'Não foi possível desligar.';
    } finally { this.salvando = false; }
  }
}
